/**
 * SoundExpertConfigTab — Config tab content for the SoundExpert node.
 *
 * ElevenLabs-inspired layout:
 *   - Top area: mode-specific prompt textarea (fills available space)
 *   - Bottom toolbar (compact, single line):
 *     - Model pill ("ElevenLabs V3")
 *     - Mode selector (Music / Text to Speech / Sound Effect)
 *     - Config summary chip -> opens SoundConfigFlyover via FlyoutPortal
 *     - Retry count + Generate button (right-aligned)
 *
 * Speech mode uses RichTextBox with @-mention support.
 * Music/SFX use plain text prompts.
 */

import { memo, useCallback, useRef, useState } from 'react'
import { RichTextBox, type RichTextBoxHandle, type RichTextBoxState } from './RichTextBox'
import { SoundConfigFlyover } from './SoundConfigFlyover'
import { FlyoutPortal } from './FlyoutPortal'
import type { SoundExpertNodeData, VoiceProfileDict } from '@ponpon/api-client'
import type { ReactFlowCanvasNode } from '../../../../hooks/useYCanvas'
import { getSoundModelLabel } from '../../../../constants/modelAliases'

// --- Types ------------------------------------------------------------------

export type SoundMode = NonNullable<SoundExpertNodeData['in_mode']>

export interface SoundExpertConfigTabProps {
  mode: SoundMode
  musicPrompt: string
  sfxPrompt: string
  speechContent: string
  duration: string
  loop: string
  lyricsMode: string
  model: string
  voiceProfile?: VoiceProfileDict
  isRunning: boolean
  retryCount: number
  canvasNodes?: ReactFlowCanvasNode[]
  onModeChange: (mode: SoundMode) => void
  onMusicPromptChange: (value: string) => void
  onSfxPromptChange: (value: string) => void
  onSpeechContentChange: (state: RichTextBoxState) => void
  onDurationChange: (value: string) => void
  onLoopChange: (value: string) => void
  onLyricsModeChange: (value: string) => void
  onModelChange: (value: string) => void
  onVoiceProfileChange?: (profile: VoiceProfileDict | undefined) => void
  onRetryCountChange: (count: number) => void
  onGenerate: () => void
  onMentionClick?: (nodeId: string) => void
}

// --- Constants --------------------------------------------------------------

const MODES: SoundMode[] = ['music', 'speech', 'sfx']

const MODE_LABELS: Record<SoundMode, string> = {
  music: 'Music',
  speech: 'Text to Speech',
  sfx: 'Sound Effect',
}

const MODE_ICONS: Record<SoundMode, string> = {
  music: '\u{1F3B5}',
  speech: '\u{1F399}️',
  sfx: '\u{1F4A5}',
}

const MODE_SHORT_LABELS: Record<SoundMode, string> = {
  music: 'Music',
  speech: 'Speech',
  sfx: 'SFX',
}

const PROMPT_PLACEHOLDERS: Record<SoundMode, string> = {
  music: 'Describe your music, e.g. upbeat jazz piano with soft drums',
  speech: '',
  sfx: 'Describe a sound effect, e.g. thunder rumbling in the distance',
}

const SPEECH_TEMPLATE = `[0.5s-6.0s] Lieutenant: "[scared] Sir, they're not going away! The strait is still closed! Should we... should we send seal?"
[6.5s-7.5s] Brump: "[firm] SEND SEALS!"`

/** Default sparks per generation when pricing is unavailable */
const BASE_SPARKS = 3

const ACCENT = {
  base: '#06b6d4',
  bg20: 'rgba(6, 182, 212, 0.2)',
  bg40: 'rgba(6, 182, 212, 0.4)',
  bg90: 'rgba(6, 182, 212, 0.9)',
}

const MODEL_DISPLAY: Record<string, string> = {
  elevenlabs_v3: getSoundModelLabel('elevenlabs_v3'),
}

// --- Config Summary Helper --------------------------------------------------

function getConfigSummary(
  mode: SoundMode,
  duration: string,
  loop: string,
  lyricsMode: string,
): string {
  if (mode === 'sfx') {
    const durLabel = duration === 'auto' ? 'Auto' : duration
    const loopLabel = loop === 'on' ? 'On' : 'Off'
    return `${durLabel} · ${loopLabel}`
  }
  if (mode === 'music') {
    const durLabel = duration === 'auto' ? 'Auto' : duration
    const lyricsLabel = lyricsMode.charAt(0).toUpperCase() + lyricsMode.slice(1)
    return `${durLabel} · ${lyricsLabel}`
  }
  // speech
  return 'Voice'
}

// --- Component --------------------------------------------------------------

const SoundExpertConfigTab = memo(function SoundExpertConfigTab({
  mode,
  musicPrompt,
  sfxPrompt,
  speechContent,
  duration,
  loop,
  lyricsMode,
  model,
  voiceProfile,
  isRunning,
  retryCount,
  canvasNodes,
  onModeChange,
  onMusicPromptChange,
  onSfxPromptChange,
  onSpeechContentChange,
  onDurationChange,
  onLoopChange,
  onLyricsModeChange,
  onModelChange: _onModelChange,
  onVoiceProfileChange,
  onRetryCountChange,
  onGenerate,
  onMentionClick,
}: SoundExpertConfigTabProps) {
  const richTextRef = useRef<RichTextBoxHandle>(null)
  const configChipRef = useRef<HTMLButtonElement>(null)
  const retryPillRef = useRef<HTMLButtonElement>(null)
  const modeChipRef = useRef<HTMLButtonElement>(null)
  const [speechInitialized, setSpeechInitialized] = useState(false)
  const [isFlyoverOpen, setIsFlyoverOpen] = useState(false)
  const [isRetryPopoverOpen, setIsRetryPopoverOpen] = useState(false)
  const [isModePopoverOpen, setIsModePopoverOpen] = useState(false)

  const closeFlyover = useCallback(() => setIsFlyoverOpen(false), [])

  const closeRetryPopover = useCallback(() => setIsRetryPopoverOpen(false), [])
  const closeModePopover = useCallback(() => setIsModePopoverOpen(false), [])

  const handleModeChange = useCallback(
    (newMode: SoundMode) => {
      onModeChange(newMode)
      setIsFlyoverOpen(false)
      setIsModePopoverOpen(false)
      // Pre-fill speech template on first switch to speech
      if (newMode === 'speech' && !speechInitialized && !speechContent) {
        setSpeechInitialized(true)
        // Set via ref after mode switch renders RichTextBox
        setTimeout(() => {
          richTextRef.current?.setText(SPEECH_TEMPLATE)
        }, 0)
      }
    },
    [onModeChange, speechInitialized, speechContent],
  )

  const isPromptEmpty =
    mode === 'music'
      ? !musicPrompt.trim()
      : mode === 'sfx'
        ? !sfxPrompt.trim()
        : !speechContent.trim()

  const generateDisabled = isRunning || isPromptEmpty
  const sparksCount = retryCount * BASE_SPARKS
  const modelDisplay = MODEL_DISPLAY[model] || model
  const configSummary = getConfigSummary(mode, duration, loop, lyricsMode)

  return (
    <div
      className="h-full flex flex-col nopan nodrag nowheel"
      onPointerDown={(e) => e.stopPropagation()}
      data-testid="sound-expert-config-tab"
    >
      {/* Prompt area -- fills available space */}
      <div className="flex-1 overflow-y-auto px-3 pt-3 pb-2 min-h-0">
        {mode === 'speech' ? (
          <RichTextBox
            containerClassName="h-full bg-surface-1 border border-border-subtle rounded p-2"
            containerTestId="speech-prompt-area"
            ref={richTextRef}
            canvasNodes={canvasNodes}
            placeholder="Write dialogue with @character mentions..."
            initialContent={speechContent || undefined}
            onContentChange={onSpeechContentChange}
            onMentionClick={onMentionClick}
            minHeight={60}
            maxHeight={120}
          />
        ) : (
          <textarea
            value={mode === 'music' ? musicPrompt : sfxPrompt}
            onChange={(e) =>
              mode === 'music'
                ? onMusicPromptChange(e.target.value)
                : onSfxPromptChange(e.target.value)
            }
            placeholder={PROMPT_PLACEHOLDERS[mode]}
            data-testid={`${mode}-prompt-input`}
            className="w-full h-full min-h-[60px] text-sm bg-surface-1 text-text-primary border border-border-subtle rounded px-2 py-1.5 resize-none focus:outline-none focus:border-primary/50 transition-colors placeholder:text-text-muted/40"
          />
        )}
      </div>

      {/* Bottom toolbar -- compact single line */}
      <div className="flex-shrink-0 border-t border-border-subtle bg-surface-0 px-3 py-2">
        {/* Flyover popover -- portal-based */}
        <FlyoutPortal
          triggerRef={configChipRef}
          isOpen={isFlyoverOpen}
          onClose={closeFlyover}
        >
          <SoundConfigFlyover
            mode={mode}
            duration={duration}
            loop={loop}
            lyricsMode={lyricsMode}
            voiceProfile={voiceProfile}
            onDurationChange={onDurationChange}
            onLoopChange={onLoopChange}
            onLyricsModeChange={onLyricsModeChange}
            onVoiceProfileChange={onVoiceProfileChange}
          />
        </FlyoutPortal>

        <div className="flex items-center gap-1.5" data-testid="sound-config-toolbar">
          {/* Model pill */}
          <span
            className="text-xxs px-2 py-1 rounded-full bg-surface-1 border border-border-subtle text-text-muted truncate"
            data-testid="model-pill"
          >
            {modelDisplay}
          </span>

          {/* Mode selector chip + popover */}
          <div data-testid="toolbar-mode-selector">
            <button
              ref={modeChipRef}
              onClick={() => setIsModePopoverOpen(!isModePopoverOpen)}
              className="flex items-center gap-1 px-2 py-1 text-xxs font-medium rounded-full bg-surface-1 border border-border-subtle text-text-muted hover:text-foreground hover:bg-surface-2 transition-colors"
              data-testid="mode-chip"
            >
              <span className="text-xxs">{MODE_ICONS[mode]}</span>
              {MODE_SHORT_LABELS[mode]}
              <svg
                className={`w-3 h-3 transition-transform ${isModePopoverOpen ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            <FlyoutPortal
              triggerRef={modeChipRef}
              isOpen={isModePopoverOpen}
              onClose={closeModePopover}
              minWidth={140}
            >
              <div className="flex flex-col gap-0.5 -m-3" data-testid="mode-popover">
                {MODES.map((m) => (
                  <button
                    key={m}
                    onClick={() => handleModeChange(m)}
                    data-testid={`toolbar-mode-btn-${m}`}
                    className={`w-full px-3 py-2 text-xs font-medium text-left transition-colors flex items-center gap-2 ${
                      mode === m
                        ? 'bg-primary text-primary-foreground'
                        : 'text-text-muted hover:text-foreground hover:bg-surface-2'
                    }`}
                  >
                    <span className="text-sm">{MODE_ICONS[m]}</span>
                    {MODE_LABELS[m]}
                  </button>
                ))}
              </div>
            </FlyoutPortal>
          </div>

          {/* Config summary chip -> opens flyover */}
          <button
            ref={configChipRef}
            onClick={() => setIsFlyoverOpen(!isFlyoverOpen)}
            className="text-xxs px-2 py-1 rounded-full bg-surface-1 border border-border-subtle text-text-muted hover:text-foreground hover:bg-surface-2 transition-colors truncate"
            data-testid="config-summary-chip"
            title={`${MODE_LABELS[mode]} settings`}
          >
            {configSummary}
          </button>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Retry Count — Collapsed Pill with Popover */}
          <div data-testid="retry-selector">
            <button
              ref={retryPillRef}
              onClick={() => setIsRetryPopoverOpen(!isRetryPopoverOpen)}
              className="px-2.5 py-1 text-xs font-medium rounded-full bg-surface-2 text-text-muted hover:text-foreground border border-border-subtle hover:border-primary/40 transition-colors whitespace-nowrap"
              data-testid="retry-pill"
            >
              {retryCount}x
            </button>

            <FlyoutPortal
              triggerRef={retryPillRef}
              isOpen={isRetryPopoverOpen}
              onClose={closeRetryPopover}
              minWidth={48}
            >
              <div className="flex flex-col gap-0.5 -m-3" data-testid="retry-popover">
                {([4, 3, 2, 1]).map((n) => (
                  <button
                    key={n}
                    onClick={() => {
                      onRetryCountChange(n)
                      setIsRetryPopoverOpen(false)
                    }}
                    className={`w-full px-3 py-1.5 text-xs font-medium text-center transition-colors ${
                      retryCount === n
                        ? 'bg-primary text-primary-foreground'
                        : 'text-text-muted hover:text-foreground hover:bg-surface-2'
                    }`}
                    data-testid={`retry-${n}x`}
                  >
                    {n}x
                  </button>
                ))}
              </div>
            </FlyoutPortal>
          </div>

          {/* Sparks Count */}
          <span className="flex items-center gap-1 text-xs text-text-muted tabular-nums" data-testid="sparks-count">
            <svg className="w-3.5 h-3.5 text-yellow-400 shrink-0" viewBox="0 0 24 24" fill="currentColor">
              <path d="M13 3L4 14h7l-2 7 9-11h-7l2-7z" />
            </svg>
            {sparksCount}
          </span>

          {/* Generate button */}
          <button
            onClick={onGenerate}
            disabled={generateDisabled}
            className="w-8 h-8 flex items-center justify-center rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ backgroundColor: 'var(--primary)' }}
            title="Generate"
            data-testid="generate-btn"
          >
            <svg className="w-4 h-4 text-primary-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 10l7-7m0 0l7 7m-7-7v18" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  )
})

SoundExpertConfigTab.displayName = 'SoundExpertConfigTab'

export { SoundExpertConfigTab, SPEECH_TEMPLATE, ACCENT as SOUND_ACCENT }
